import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useAPI from '../hooks/useAPI';
import { getFieldFeedback, downloadFieldFeedbackExport } from '../services/api';

const SEV = { red: '#e74c3c', yellow: '#f39c12', green: '#27ae60' };
const PAGE = 50;

export default function FieldFeedback() {
  const navigate = useNavigate();
  const [country, setCountry] = useState('');        // '' = All
  const [stateF, setStateF] = useState('All');
  const [site, setSite] = useState('All');
  const [errorCode, setErrorCode] = useState('All');
  const [status, setStatus] = useState('All');       // All | confirmed | waiting
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [visible, setVisible] = useState(PAGE);
  const [exporting, setExporting] = useState(false);
  const [tip, setTip] = useState(null);              // {text, x, y}

  const params = { country: country || 'All', state: stateF, site, error_code: errorCode, status };
  const { data, loading, error } = useAPI(
    () => getFieldFeedback(params),
    [country, stateF, site, errorCode, status]
  );

  const facets = data?.facets || { countries: [], states_by_country: {}, sites: [], error_codes: [] };
  const rows = data?.rows || [];

  const stateOpts = useMemo(() => {
    if (!country) {
      const all = new Set();
      Object.values(facets.states_by_country || {}).forEach(arr => arr.forEach(s => all.add(s)));
      return Array.from(all).sort();
    }
    return facets.states_by_country?.[country] || [];
  }, [facets, country]);

  const siteOpts = useMemo(() => (facets.sites || [])
    .filter(s => (!country || s.country === country) && (stateF === 'All' || s.state === stateF))
    .map(s => s.name), [facets, country, stateF]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    const f = sortField, dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let va = a[f], vb = b[f];
      const na = typeof va === 'number', nb = typeof vb === 'number';
      if (va == null) va = (na || nb) ? -Infinity : '';
      if (vb == null) vb = (na || nb) ? -Infinity : '';
      if ((na || nb) && !isNaN(va) && !isNaN(vb)) return (Number(va) - Number(vb)) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return arr;
  }, [rows, sortField, sortDir]);

  const shown = sorted.slice(0, visible);

  const onSort = (f) => {
    if (sortField === f) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(f); setSortDir(f === 'date' || f === 'feedback_date' ? 'desc' : 'asc'); }
  };
  const arrow = (f) => (sortField === f ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const reset = (fn) => (v) => { fn(v); setVisible(PAGE); };
  const onCountry = (v) => { setCountry(v); setStateF('All'); setSite('All'); setVisible(PAGE); };

  const fmtDate = (ms) => {
    if (!ms) return '—';
    try {
      return new Date(Number(ms)).toLocaleString('en-GB',
        { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch (e) { return String(ms); }
  };
  const fmtParts = (p) => {
    if (!p) return '—';
    try {
      const j = JSON.parse(p);
      if (Array.isArray(j)) return j.map(x => `${x.part_id || x.partId || ''}×${x.qty || 1}`).join(', ') || '—';
    } catch (e) { /* plain string */ }
    return String(p);
  };
  const fmtMin = (m) => (m == null ? '—' : `${m} min`);
  const fmtHandling = (ms) => {
    if (ms == null) return '—';
    const sec = Math.round(Number(ms) / 1000);
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60), s2 = sec % 60;
    if (m < 60) return s2 ? `${m}m ${s2}s` : `${m}m`;
    const h = Math.floor(m / 60), mm = m % 60;
    return mm ? `${h}h ${mm}m` : `${h}h`;
  };

  const doExport = async () => {
    setExporting(true);
    try { await downloadFieldFeedbackExport(params); }
    catch (e) { alert('Export failed: ' + e.message); }
    finally { setExporting(false); }
  };

  const openSummary = (r) => {
    const p = new URLSearchParams({
      project: String(r.project_id), robot: String(r.robot_number), from: 'field-feedback',
    });
    navigate(`/robot-summary?${p.toString()}`);
  };

  const COUNTRIES = [{ k: '', l: 'All' }, { k: 'Israel', l: 'Israel' }, { k: 'India', l: 'India' }];

  return (
    <div onMouseMove={(e) => tip && setTip(t => t && ({ ...t, x: e.clientX, y: e.clientY }))}>
      <div style={st.head}>
        <div>
          <h1 style={st.h1}>Field Feedback</h1>
          <p style={st.sub}>{data?.total || 0} feedback record{(data?.total || 0) === 1 ? '' : 's'}
            {' '}· Confirmed + Waiting</p>
        </div>
        <button onClick={doExport} disabled={exporting || !rows.length} style={st.exportBtn}>
          {exporting ? 'Exporting…' : '⬇  Export to Excel'}
        </button>
      </div>

      {/* Filters */}
      <div style={st.filters}>
        <div style={st.countryGroup}>
          {COUNTRIES.map((o, i, arr) => (
            <button key={o.k || 'all'} onClick={() => onCountry(o.k)}
              style={{
                ...st.cBtn, ...(country === o.k ? st.cBtnActive : {}),
                borderTopLeftRadius: i === 0 ? 6 : 0, borderBottomLeftRadius: i === 0 ? 6 : 0,
                borderTopRightRadius: i === arr.length - 1 ? 6 : 0, borderBottomRightRadius: i === arr.length - 1 ? 6 : 0,
              }}>{o.l}</button>
          ))}
        </div>
        <label style={st.lbl}>State
          <select value={stateF} onChange={(e) => { reset(setStateF)(e.target.value); setSite('All'); }} style={st.sel}>
            <option value="All">All</option>
            {stateOpts.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label style={st.lbl}>Site
          <select value={site} onChange={(e) => reset(setSite)(e.target.value)} style={st.sel}>
            <option value="All">All</option>
            {siteOpts.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      {error && <div style={st.err}>Failed to load: {error}</div>}
      {loading ? <div style={st.loading}>Loading field feedback…</div> : (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th} onClick={() => onSort('date')}>Date{arrow('date')}</th>
                <th style={st.th} onClick={() => onSort('project_name')}>Site{arrow('project_name')}</th>
                <th style={st.th} onClick={() => onSort('robot_number')}>Robot{arrow('robot_number')}</th>
                <th style={st.th}>
                  <span onClick={() => onSort('error_code')} style={{ cursor: 'pointer' }}>Error code{arrow('error_code')}</span>
                  <select value={errorCode} onChange={(e) => reset(setErrorCode)(e.target.value)} style={st.thSel}>
                    <option value="All">All</option>
                    {(facets.error_codes || []).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </th>
                <th style={st.th} onClick={() => onSort('severity')}>Verdict{arrow('severity')}</th>
                <th style={st.th} onClick={() => onSort('feedback_date')}>Date Feedback{arrow('feedback_date')}</th>
                <th style={st.th} onClick={() => onSort('active_handling_ms')}>Time to fix{arrow('active_handling_ms')}</th>
                <th style={st.th}>
                  <span onClick={() => onSort('status')} style={{ cursor: 'pointer' }}>Status{arrow('status')}</span>
                  <select value={status} onChange={(e) => reset(setStatus)(e.target.value)} style={st.thSel}>
                    <option value="All">All</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="waiting">Waiting for review</option>
                  </select>
                </th>
                <th style={st.th} onClick={() => onSort('tech_name')}>Technician{arrow('tech_name')}</th>
                <th style={st.th}>Q1 · Q2 · Q3 + parts</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={r.id} style={i % 2 ? st.trOdd : st.trEven}>
                  <td style={st.td}>{fmtDate(r.date)}</td>
                  <td style={st.td}>{r.project_name || '—'}</td>
                  <td style={st.td}>{r.robot_number != null ? `#${r.robot_number}` : '—'}</td>
                  <td style={st.td}>{r.error_code || '—'}</td>
                  <td style={st.td}>
                    <span style={{ ...st.chip, background: SEV[r.severity] || '#95a5a6' }}
                      onClick={() => openSummary(r)}
                      onMouseEnter={(e) => setTip({ text: r.rca_brief || r.verdict_title || '', x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setTip(null)}>
                      {r.verdict_title || (r.severity || '').toUpperCase() || '—'}
                    </span>
                  </td>
                  <td style={st.td}>{fmtDate(r.feedback_date)}</td>
                  <td style={st.td}>{fmtHandling(r.active_handling_ms)}</td>
                  <td style={st.td}>
                    <span style={r.status === 'confirmed' ? st.stOk : st.stWait}>
                      {r.status === 'confirmed' ? 'Confirmed' : 'Waiting'}
                    </span>
                  </td>
                  <td style={st.td}>{r.tech_name || '—'}</td>
                  <td style={{ ...st.td, ...st.qCell }}>
                    {r.status === 'waiting'
                      ? <span style={{ color: '#b2bec3', fontStyle: 'italic' }}>no write-up yet</span>
                      : <div>
                        {r.q1 && <div><b>Q1:</b> {r.q1}</div>}
                        {r.q2 && <div><b>Q2:</b> {r.q2}</div>}
                        {r.q3 && <div><b>Q3:</b> {r.q3}</div>}
                        <div style={{ color: '#0984e3' }}><b>Parts:</b> {fmtParts(r.parts)}</div>
                      </div>}
                  </td>
                </tr>
              ))}
              {!shown.length && <tr><td colSpan={10} style={st.empty}>No feedback for the current filters.</td></tr>}
            </tbody>
          </table>

          {visible < sorted.length && (
            <div style={st.moreWrap}>
              <button style={st.moreBtn} onClick={() => setVisible(v => v + PAGE)}>
                Load 50 more ({sorted.length - visible} remaining)
              </button>
            </div>
          )}
          <div style={st.count}>Showing {Math.min(visible, sorted.length)} of {sorted.length}</div>
        </div>
      )}

      {tip && tip.text && (
        <div style={{ ...st.tooltip, left: Math.min(tip.x + 14, window.innerWidth - 460), top: Math.min(tip.y + 14, window.innerHeight - 220) }}>
          {tip.text}
        </div>
      )}
    </div>
  );
}

const st = {
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  h1: { fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { fontSize: 14, color: '#636e72', marginTop: 4 },
  exportBtn: { background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  filters: { display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', margin: '18px 0' },
  countryGroup: { display: 'inline-flex' },
  cBtn: { padding: '8px 16px', background: '#fff', border: '1px solid #dfe6e9', color: '#636e72', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderLeft: 'none' },
  cBtnActive: { background: '#2d3436', color: '#fff', borderColor: '#2d3436' },
  lbl: { display: 'flex', flexDirection: 'column', fontSize: 12, color: '#636e72', fontWeight: 600, gap: 4 },
  sel: { padding: '7px 10px', borderRadius: 6, border: '1px solid #dfe6e9', fontSize: 13, minWidth: 160 },
  tableWrap: { background: '#fff', borderRadius: 8, border: '1px solid #ecf0f1', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', background: '#f8f9fa', borderBottom: '2px solid #ecf0f1', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 700, color: '#2d3436', verticalAlign: 'top' },
  thSel: { display: 'block', marginTop: 6, padding: '3px 6px', borderRadius: 4, border: '1px solid #dfe6e9', fontSize: 11, fontWeight: 400, maxWidth: 150 },
  td: { padding: '9px 12px', borderBottom: '1px solid #f1f2f6', verticalAlign: 'top' },
  qCell: { maxWidth: 380, fontSize: 12, lineHeight: 1.5 },
  trEven: { background: '#fff' },
  trOdd: { background: '#fbfcfd' },
  chip: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  stOk: { color: '#27ae60', fontWeight: 700 },
  stWait: { color: '#f39c12', fontWeight: 700 },
  tooltip: { position: 'fixed', zIndex: 1000, maxWidth: 440, background: '#2d3436', color: '#f5f6fa', padding: '10px 14px', borderRadius: 8, fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap', boxShadow: '0 6px 24px rgba(0,0,0,0.3)', pointerEvents: 'none' },
  moreWrap: { textAlign: 'center', padding: 16, borderTop: '1px solid #f1f2f6' },
  moreBtn: { background: '#0984e3', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  count: { textAlign: 'center', padding: '0 0 14px', fontSize: 12, color: '#b2bec3' },
  loading: { padding: 40, color: '#999' },
  err: { padding: 14, background: '#ffeaa7', borderRadius: 6, color: '#d63031', margin: '12px 0' },
  empty: { padding: 30, textAlign: 'center', color: '#b2bec3' },
};
